/**
 * Dev-only helper for previewing `AuthErrorScreen` without having to
 * manufacture a real Auth0 error (wrong-password errors never even reach the
 * app — they're handled entirely on Auth0's hosted login page — and a
 * genuine token-exchange failure normally requires interrupting a network
 * call at exactly the right moment).
 *
 * Every Auth0 failure mode now renders the same single "unauthorized"
 * explanation (see helpers/auth_errors.ts), so there's no need for a preset
 * per OAuth error code anymore — there are only two visually distinct
 * screens left to preview:
 *   - `unauthorized`    — the standard screen, offers "Try Again".
 *   - `token_exchange`  — the non-retryable variant, offers
 *     "Reset & Return to Login" instead, to avoid a redirect loop.
 *
 * Visit any private route with `?debug_auth_error=<key>` appended while
 * running in development, e.g.:
 *
 *   http://localhost:8004/?debug_auth_error=unauthorized
 *   http://localhost:8004/?debug_auth_error=token_exchange
 *
 * `process.env.NODE_ENV` is inlined at build time (see build.js), so this
 * whole module is dead code in production bundles — it can never fire for
 * real users.
 */

export type DebugAuthErrorKey = 'unauthorized' | 'token_exchange';

type Auth0LikeError = Error & { error?: string; error_description?: string };

const withOAuthShape = (message: string, code: string): Auth0LikeError => {
  const error = new Error(message) as Auth0LikeError;
  error.error = code;
  error.error_description = message;
  return error;
};

const DEBUG_AUTH_ERROR_PRESETS: Record<DebugAuthErrorKey, () => Error> = {
  unauthorized: () => withOAuthShape('missing required role for this dashboard', 'unauthorized'),
  token_exchange: () => new Error('Unable to exchange code for tokens: invalid state'),
};

export const DEBUG_AUTH_ERROR_KEYS = Object.keys(DEBUG_AUTH_ERROR_PRESETS) as DebugAuthErrorKey[];

/**
 * Returns a synthetic Auth0 error to feed through the real
 * `getAuthErrorInfo()` mapping, or `null` when not in development, when
 * running outside a browser, or when no `debug_auth_error` param is present.
 *
 * An unrecognized key falls back to the `unauthorized` preset, since that's
 * also what every real unmapped Auth0 error code resolves to.
 */
export const getDebugAuthError = (): Error | null => {
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  if (typeof window === 'undefined') {
    return null;
  }

  const key = new URLSearchParams(window.location.search).get(
    'debug_auth_error'
  ) as DebugAuthErrorKey | null;

  if (!key) {
    return null;
  }

  const factory = DEBUG_AUTH_ERROR_PRESETS[key];
  return factory ? factory() : DEBUG_AUTH_ERROR_PRESETS.unauthorized();
};
